<script lang="ts" setup>
	import type { ClassNameValue } from 'tailwind-merge'
	import type { uiClass } from './index.vue'
	import type { IconLibProps } from '@/components/Tools/icon.vue'

	const {
		modalClass = '',
		useCancelBtn = true,
		capitalizeHeader = true,
		type = 'error',
		wrapperClass = 'py-2'
	} = defineProps<{
		btnDisabled?: boolean
		header?: string
		paragraph?: string
		wrapperClass?: ClassContext
		processing?: boolean
		modalButtonText?: string
		closeOnBg?: boolean
		capitalizeHeader?: boolean
		useCancelBtn?: boolean
		show: boolean
		iconProps?: (Partial<IconLibProps> & { wrapperClass?: ClassNameValue }) | null
		type?: 'warning' | 'error'
		modalClass?: ClassContext
		ui?: Partial<uiClass>
	}>()
	const defaultIconProps = computed(() => ({
		dir: 'fill',
		icon: type === 'error' ? 'declined' : 'warning'
	}))
	const emit = defineEmits<{
		(param: 'close', arg: boolean): void
		(param: 'done', arg?: Function): void
		(param: 'update:show', arg: boolean): void
	}>()
	const closeModal = () => {
		emit('close', false)
		emit('update:show', false)
	}
</script>

<template>
	<div>
		<dialogs
			:show="show"
			:ui="{ ...ui, modal: cn('pb-5', ui?.modal) }"
			:modal-class="cn(`max-w-[600px] sm:min-h-[500px] flex-col items-center justify-center ${modalClass}`)"
			:use-cancel-btn="useCancelBtn"
			@change="closeModal"
		>
			<div class="flex flex-col items-center justify-center gap-x-5 gap-y-1">
				<div
					:class="
						cn(
							'light-shadow mb-8 inline-flex min-h-[80px] w-[80px] items-center justify-center rounded-full border',
							iconProps?.wrapperClass ?? ''
						)
					"
				>
					<slot :close="closeModal" name="icon">
						<lazy-tools-icon
							v-bind="iconProps"
							:dir="iconProps?.dir || defaultIconProps.dir"
							:icon="iconProps?.icon || defaultIconProps.icon"
						/>
					</slot>
				</div>

				<div :class="wrapperClass" class="text-center">
					<h4
						class="pb-7 text-xl font-[700] tracking-wide text-blue-950 md:text-[28px]"
						:class="{ capitalize: capitalizeHeader }"
					>
						<slot name="header">
							<span v-html="header" />
						</slot>
					</h4>
					<slot :close="closeModal">
						<div class="max-w-lg text-sm font-medium text-gray-500 md:text-base md:leading-[25.888px]">
							<slot name="paragraph">
								<span v-html="paragraph" />
							</slot>
						</div>
					</slot>
				</div>

				<div class="mb-8 w-full">
					<slot name="p" />
				</div>
				<slot :close="closeModal" name="button">
					<a-button
						class="default-btn 00 w-full justify-center !bg-primary-2 font-semibold tracking-wide !text-white disabled:!bg-primary-2"
						:disabled="btnDisabled"
						:loading="processing"
						data-test="process"
						@click="emit('done')"
					>
						{{ modalButtonText }}
					</a-button>
				</slot>
			</div>
		</dialogs>
	</div>
</template>
